import { FeatureFlagsHelper } from './FeatureFlagsHelper';


/**
 * There are two types of "feature flags": Development-only flags, and permanent features.
 *
 * "Development" feature flags (features that are being built, for which the flags will be
 * removed once the feature is shipped) belong in this file.
 * ( more info here: https://mytableau.tableaucorp.com/x/3aWaB )
 *
 * Flags for permanent features should not live here; instead, those flags should live on the server
 * or site objects of ISessionInfo, depending on whether they can be toggled at the server or site
 * level.
 *
 * One day this will move from a guideline to a requirement and you will get an urgent bug to
 * move your flag, if it falls into that category.
 *
 * This module is a global object that is as low-level in the app as possible,
 * so we can theoretically put any parameter of the application behind a flag. Flags are specified
 * here, but can be overridden at runtime via URL query parameters.

 * ////////////////////////////////////////////////////////////
 * ///////////READ THIS BEFORE ADDING ANY NEW FLAGS////////////
 * ///////////READ THIS BEFORE ADDING ANY NEW FLAGS////////////
 * ///////////////////////////////////////////////////////////

 * Note: If you defined your feature flag in the monolith, the first letter of your feature flag has
 * be lower case here, and "Enabled" has added to the suffix. Even if you define it with an uppercase
 * first letter elsewhere, it will be transformed to have a lower case first letter and an "Enabled"
 * suffix when delivered to VPC for overrides.
 * See https://confluence.internal.salesforce.com/spaces/devprocess/pages/529054092/Feature+Flags+in+Server+Back-end
 */


export module FeatureFlags {

  /**
   * Shows the Tableau Knowledge nav item. Defaults off; the server overwrites
   * this with the real per-site value from the TableauKnowledgeNav flag,
   * delivered in the SessionInfo featureFlags section.
   */
  export const tableauKnowledgeNavEnabled = false;

  /**
   * Allows direct assignment of a remote query agent to a data source
   * Created: 2017-03-16 bjudkins
   */
  // remove: TFS 808766
  export const bridgeLiveDirectAssignEnabled = false;

  /**
   * Enables the feature where user can use their OAuth credential to refresh WDC data souces.
   */
  // Created: 2015-10-16 wechen
  // Enabled: 2016-01-07 wechen
  // Disabled: 2016-02-09 mbgumport
  // remove: TFS 808766
  export const oauthWDCServerRefreshEnabled = false;

  // Enables creation of self service schedules
  // Created: 2016-10-24 dcoonan
  // remove: TFS 808776
  // Code pointed this to being used as a server setting instead of a FF.
  // See IFeatureFlagsDoNotUse.ts
  export const selfServiceSchedulesEnabled = false;

  // Enables the feature to create user time zone
  // Created: 2017-03-20 nchenglie
  export const userTimeZoneEnabled = false;

  /**
   * Enables the job management feature.
   */
  // Created: 2018-09-19 nchenglie
  export const jobManagementEnabled = false;

  /*
   * Shows the Certification information on the DS place page.
   */
  // Created: 2017-04-18 eprescott
  // remove: TFS 808701
  export const datasourceCertificationEnabled = true;

  /*
   * Shows a link to disk space usage admin view if enabled
   */
  // Created: 2016-01-11 jchar
  export const lowDiskSpaceEnabled = false;

  /*
   * Display Tableau desktop license usage summary if enabled
   */
  // Created: 2016-03-31 jili
  // Enabled: 2016-04-04 jili
  // remove: TFS 808741
  export const desktopReportingEnabled = true;

  /*
   * Allows subscriptions to be triggered based off of a threshold condition.
   */
  // Created: 2016-06-08 cpredmore
  // Enabled: 2016-09-29 cpredmore
  // Disabled: 2016-10-28 abrandon (TFS: 581467)
  // Enabled: 2017-01-24 tyakushev
  // remove: TFS 808745
  export const alertOnThresholdConditionEnabled = true;

  /*
   * Allows Tableau support users to be added to a Tableau Cloud site.
   */
  export const addSupportUserEnabled = false;

  /**
   * Allows the server admin to enable the cache warmup feature
   *
   */
  // Created: 2016-09-23 gfilpus
  // Enabled: 2017-03-20 gfilpus
  // remove: TFS 774533
  export const cacheWarmup = true;

  /**
   * Enable "Refresh Now" menu item for remotely-refresh data sources
   * Created: 2016-12-08 bjudkins
   */
  // remove: 808766
  export const dataSyncRefreshNowEnabled = false;

  /**
   * Uses a new, vizportal-based getting started screen, rather than rendering a marketing page.
   * Created: 2016-09-29 cshiraishi
   * Enabled: 2016-11-22 cshiraishi
   */
  // Remove: TFS 775870
  export const newGettingStartedScreen = true;

  /**
   * Shows a custom banner for opening in app for mobile devices on the login page.
   */
  // Created: 2017-03-15
  // Enabled: 2017-03-17
  // remove: TFS 808698
  export const customAppBanner = true;

  /**
   * Shows 'what's new' menu option
   */
  // Created: 2017-04-06 klin
  export const whatIsNewMenuOptionEnabled = false;

  /**
   * Enable renaming views on server (without going into web authoring)
   */
  // Created: 2017-08-07 janlee
  export const renameViewsEnabled = false;

  /**
   * Show hyper instead of dataengine.
   */
  //Created: 2017-05-8 mroberts
  // remove: TFS 808750
  export const hyperOnServerEnabled = false;

  /**
   * Flag to know if new tabadmin services (TSM) feature is enabled (backing feature flag is TabadminService)
   */
  // Created: 2017-05-18 acarre
  // remove: TFS 808750
  export const tableauServicesManagerEnabled = false;

  /**
   * Display data connection tooltip in viz view.
   */
  // Created: 2017-08-16 shubbard
  export const dataConnectionTooltipInVizView = true;

  /**
   * Send analytics info to server
   */
  // Created: 2017-11-21 jfitzgerald
  export const OnlineAnalyticsPipelineEnabled = false;

  /**
   * Display SCIM settings in the authentication tab
   * See: http://l/tfs/697361
   */
  // Created: 2018-02-21 mvafadost
  export const scimApiTokenEnabled = false;

  /**
   *  Show new site settings tab for extensions
   */
  // created: 2018-4-4 jfitzgerald
  export const extensionsSettingsTabEnabled = true;

  /**
   *  Enable/Disable Mentions in Commenting
   */
  // Created: 2018-4-30 nranganna
  export const commentingMentionsEnabled = true;

  /**
   * Show suspend subscription status and allow users to resume suspended subscriptions
   */
  // Created: 2018-09-11 jvanduyne
  // Enabled: 2018-10-29 jvanduyne
  export const suspendSubscriptions = true;

  /**
   * Define policies that all new passwords must comply with.
   */
  // Created: 2018-10-31 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const passwordPoliciesEnabled = true;

  /**
   * TFS852547 - Sheets as Tabs Dashboards should preview like Phoenix
   * Mobile Web shows these dashboards with tabs.  Phoenix shows them without tabs.
   */
  // Created: 2018-12-06 majung
  export const SheetsAsTabsDashboardsPreviewLikePhoenix = true;

  /**
   * Make the password expire after a certain amount of days and force the user to reset it.
   */
  // Created: 2018-11-16 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthPasswordExpirationEnabled = true;

  /**
   * Block the user after many failed login attempts and limit the rate of consecutive failed attempts.
   */
  // Created: 2018-12-19 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthLoginRateLimitingEnabled = true;

  /**
   * Allow the user to request RefreshTokens to use in API automation.
   */
  // Created: 2019-04-04 fpagliaricci
  export const personalAccessTokensEnabled = false;

  /**
   * Show a page of recommended vizzes.
   */
  // Created: 2018-12-20 dsimpson
  // Enabled: 2019-06-17 xmao
  export const vizRecommendationsEnabled = true;

  /*
   * Display Tableau IBA license usage summary if enabled
   */
  // Created: 2019-03-01 antsmith
  export const ibaReportingEnabled = false;

  /**
   * Shows the mobile biometrics setting
   */
  // created: 2019-03-18 mmathews
  export const showMobileBiometricsSetting = false;

  /*
   * Share dialog in Vizportal Client for sharing view, workbook, project, datasource
   * Created: 2019-04-11 rkandhal
   * Enabled: 2019-08-02 saramirez
   */
  export const newShareDialogEnabled = true;

  /*
   * Enables the sign in note
   */
  // Created: 2019-04-17 rjoshi
  export const signInNoteEnabled = false;

  /**
   * Enables AFE controls in the settings page
   */
  export const alternativeFederationEngineUserInterface = false;

  /**
   * Enables EAR for flows
   */
  // Created: 2019-05-10 vhalitsyn
  export const maestroEarEnabled = false;

  /**
   * Enable the User Visibiilty feature
   */
  // Created: 2019-06-25 sfan
  export const userVisibilityEnabled = false;

  /**
   * Request Access feature flag
   */
  // Created: 2019-06-19 ayavnilovitch
  //export const requestAccessEnabled = false;

  /**
   * Request Access For AI feature flag
   */
  // Created: 2026-09-17 llin
  export const requestAccessForAIEnabled = false;

  /**
   * Enable the all content types tab in search results, powered by crass.
   * Created 2019-09-6 vrobinweiler, #dev-voltron
   */
  export const crassSearch = false;

  /*
   * Enable/Disable the 19.4 UI elements for Job Management
   */
  // Created: 2019-07-24 arama
  export const jobManagement194UIEnabled = false;

  /**
   * Enable/Disbale Managing the ability to create "Run Now" jobs
   */
  // Created: 2019-08-13 arama
  export const enableRunNowEnabled = false;

  /*
   * Enables last modified by user for workbook and datasource
   */
  //Created: 2019-09-19 sadhikari
  export const modifiedByUserEnabled = false;

  /**
   * Allow Server Side Refresh via Live Connection to Bridge client when refreshing extracts
   * Created: 2019-09-27 fuliu
   */
  // remove: TFS 986397
  export const bridgeServerSideRefresh = false;

  /**
   * Enables using the SSS dialog to edit admin schedules
   */
  // Created: 2019-10-16 arama
  export const adminSelfServiceSchedulesEnabled = false;

  /**
   * Gates the Jobs Page content column, Task Details page, and Job History section.
   */
  // Created: 2026-06-16 ebrose
  export const backgroundJobsContentAndTaskDetailsEnabled = false;

  /**
   * Enables incremental refresh
   */
  // Created: 2019-10-29 hchen

  export const incrementalRefresh = true;

 // Enables add data quality warning Tooltip flag for linked tasks
  export const linkedTaskDQWTooltipEnabled = true;

  /**
   * Increases the linked-task step limit from 20 to 50.
   * Server flag: prep_increaseLinkedTaskLimit (codegen/coredata/features/maestro/features-maestro.data).
   */
  // Created: 2026-07-14 jperkins (W-21815163)
  export const prep_increaseLinkedTaskLimitEnabled = true;

  /**
   * Enables site quota feature
   */
  // Created: 2019-10-30 sanaik
  export const siteQuotaEnabled = false;

  /**
   * Enables published connection features
   */
  // Created: 2019-11-19 phan
  export const publishedConnectionEditorEnabled = false;

  /**
   * Enables Upload Flow feature from the Create content button
   */
  // Created: 2026-04-24 anlin
  export const uploadFlowFromWebEnabled = false;

  /**
   * Enables Upload Workbook feature from the Creat content button
   */
  // Created: 2020-01-03 jwonglam
  export const uploadWorkbookFromWebEnabled = false;

  /**
   * Enables Merge/Dedupe feature in Tableau Catalog (Data Catalog)
   */
  // Created: 2020-01-08 jweill
  export const mergeDedupeDatabasesEnabled = false;

  /**
   * Enables published connection content management ship-point 1
   */
  // Created: 2020-01-15 achristian
  export const publishedConnectionContentManagementEnabled = false;

  /**
   * Enables Server Overview Admin View
   */
  // Created: 2020-01-23 sanaik
  export const serverOverviewAdminViewEnabled = false;

  /**
   * Allows the site admin to set a default site time zone.
   */
  // Created: 2020-03-09 achristian
  export const siteTimeZoneEnabled = true;

  /**
   * Enabled Prep Extensions site/server settings to control Einstein
   */
  export const prepExtensionsEinstein = true;

  /**
   * Enables the Tableau Packages (.tpkg) site-admin UI under Settings -> Packages.
   * Server flag: Packages (codegen/coredata/features/devplat/features-devplat.data).
   */
  export const packagesEnabled = false;

  /**
   * Enables the Independent Tableau Packages (.tpkg) site-admin UI under Settings -> Packages.
   * Server flag: Packages (codegen/coredata/features/devplat/features-devplat.data).
   */
  export const independentPackagesEnabled = false;


  /**
   * Surfaces the Packages tab to Admin Profile holders granted the Package resource,
   * not just self/admins. Requires packagesEnabled. Mirrors the server-side
   * AdminProfilesForPackages FF that gates Package enforcement in PackageService, and
   * follows the adminProfilesForSiteSettingsEnabled precedent (separate flag so
   * admin-profile access rolls out independently of the base Packages feature).
   */
  export const adminProfilesForPackagesEnabled = false;

  /**
   * Enables the AllowedExtensionOriginsSection Site Settings UI for editing the
   * extensionPackageAllowedOrigins site setting.
   * Server flag: ExtensionPackageAllowedOriginsSiteSetting
   * (codegen/coredata/features/essentials/features-essentials.data).
   */
  export const extensionPackageAllowedOriginsSiteSettingEnabled = true;

  /**
   * Enables enabling/disabling extract refresh auto-suspend feature for data sources
   */
  // Created: 2022-12-16 ross.bechtel
  export const extractRefreshPauseEnabledDatasources = true;

  /**
* Enables/Disables extract refresh permission capability feature for non admins and non content owners
*/
  // Created: 2024-10-15 skavuri
  export const allowNonAdminRefreshExtractsEnabled = true;

  /**
   * Enables/Disables non-admin users with allowExtractRefresh capability to manage extract refresh schedules
   */
  // Created: 2025-03-05 yigang.yao
  export const allowNonAdminManageExtractSchedulesEnabled = false;

  /**
   * Enables ability to opt in/out of extract refresh failure notifications for non admins and non content owners
   */
  // Created: 2024-11-06 ross.bechtel
  export const allowNonAdminRefreshExtractNotificationsEnabled = true;

  /**
   * Enables workbook autosave
   */
  // Created: 2021-08-09 michelle.cheung
  export const webAuthoringAutoSaveEnabled = false;

  /**
   * Enables Ask Data Usage and Performance Dashboards for Server Admins
   */
  // Created: 2020-06-22 dhruv.khanna
  export const askDataPerformanceDashboardEnabled = false;

  /**
   * Enables the Site Expiration Notifications
   */
  // Created: 2020-10-19 qzhuang
  export const siteExpirationNotificationEnabled = false;

  /**
   * Enables Data Quality Warning subscriptions
   */
  // Created: 2020-10-26 odangat
  export const writableFieldDescriptionsEnabled = true;

  /*
   * Enables OAuthClients Registry components
   */
  // Created: 2020-11-25 jwonglam, jinli
  export const siteLevelOAuthClient = false;

  /**
   * Enables searchV2 to show expected UI for semantic search
   */
  // Created: 2020-12-23 kzhuang
  export const searchV2 = true;

  /**
   * Enables International Telemetry for language and locale
   */
  // Created: 2020-12-18 jjun
  export const intlTelemetry = true;

  /**
   * Enables on premise governance VPC components
   */
  // Created: 2021-03-09 nchenglie
  export const onPremiseGovernanceEnabled = true;

  /**
   * Enables the bridge server triggered refresh feature for file based datasources
   */
  // Created: 2021-03-24 amutyala
  export const bridgeServerTriggeredClientSideRefreshForFileBasedDatasourceEnabled = false;

  /**
   * If SLO for OnPrem SiteSAML is enabled.
   */
  // Created: 2021-04-21 fpagliar
  export const siteSamlSLOEnabled = false;

  /**
   * If the user identity migration feature is enabled.
   */
  // Created: 2021-05-12 fpagliar
  export const identityMigrationEnabled = false;

  /**
   * If the identity pools feature is enabled.
   */
  // Created: 2022-06-01 fpagliar
  export const identityPoolsEnabled = false;

  /**
   * Enables handling of monolith CDMS workbookdrafts API response format
   */
  // Created: 2026-01-30 sadhikari
  export const enableCDMSAndWPSMonolithMigrationEnabled = false;

  /**
   * Enable UI for CachingPolicies
   */
  // Created: 2021-04-23 dguo
  export const queryPipelineDataFreshnessUISupported = false;

  /**
   * controls Explain Data site setting
   */
  // Created: 2021-05-20 jubooth
  // Created: 2021-07-09 jubooth
  export const explainDataSiteControl = true;

  /**
   * Enable UI for Slack integration setting
   */
  // Created: 2021-05-14 ayavnilovitch
  export const userNotificationSlackChannel = false;

  /**
   * Enable UI for MS Teams UNP setting
   */
  // Created: 2022-11-30 mshaver
  export const msTeamsUserNotification = false;

  /**
   * controls Explain Data permission
   */
  // Created: 2021-05-27 jubooth
  // Enabled: 2021-07-09 jvanduyne
  export const explainDataPermissionEnabled = true;

  /**
   * Enable banner design and disable toast service for trial user (Buy Now function)
   */
  // Created: 2021-06-16 yanyuzhong
  export const trialBannerDesignEnabled = false;

  /**
   * Enables revision history feature for virtual (published) connections
   */
  // Created: 2021-06-29 whudson
  export const vconnRevisionHistoryEnabled = true;

  /**
   * Enables field description inheritance for lineage place pages in Data Catalog
   */

  /**
   * Enables field description inheritance for lineage place pages in Data Catalog
   */
  // Created: 2021-07-27 jweill
  export const fieldDescriptionInheritanceLineageEnabled = false;

  /**
   * Enables deploying the edge pool registry service and enable multiple edge (bridge) pools
   */
  // Created: 2021-08-04 achristian
  export const edgeMultiPoolEnabled = true;

  /*
   * Enables quick search empty state quick links
   */
  // Created: 2021-08-09 amedina
  export const searchQuickLinks = true;

  /*
   * Enables Search product metrics feature.
   */
  // Created: 2022-05-18 llin
  export const searchProductMetrics = true;

  /*
   * Enables Search EPT (experienced page time) measuring feature.
   */
  // Created: 2022-05-18 llin
  export const searchEPT = true;

  /*
   * Enables Offspring Post MVP 22.4 features.
   */
  // Created: 2022-08-01 llin
  export const offspringPostMVP224 = true;

  /*
   * Enables the recent unique users to be the most common of the last 100
   */
  // Created: 2021-08-20 mcolclough
  export const aggregateRecentUsers = false;

  /**
   * Enables flow creation for Catalog spaces e.g. External Assets, and asset place pages.
   */
  // Created: 2021-08-24 jalee
  export const flowsFromCatalogEnabled = true;

  /*
   * Enables recent searches to show in the quick links box
   */
  // Created: 2021-08-25 mcolclough
  export const recentSearches = false;


  /*
   * Enables the Lineage tab on published/virtual connections and pconn/vconn tables
   */
  // Created: 2021-09-15 ehaynes
  export const publishedConnectionsLineageEnabled = true;

  export const relevantAssetsEnabled = true;

  /*
   * Enables some of the UI for linked tasks SP2
   */
  // Created: 2021-10-18 jsigo
  export const linkedTasksSP2Enabled = true;

  /*
   * Enables inifnite scroll for site users grid
   */
  // Created: 2021-11-17 rjoshi
  export const UserGridInfiniteScrollEnabled = true;

  /*
   * Enables inifnite scroll for server users grid
   */
  // Created: 2021-11-17 rjoshi
  export const serverUserSolrMigrationEnabled = true;

  /**
   * Enables UX around databases in projects
   */
  // Created 2021-11-9 jalee
  export const databasesInProjectsEnabled = true;

  /**
   * Enables broadcast service for the site
   */
  // Created 2021-11-22 ltang
  export const broadcastEnabled = false;

  /**
   * Enables Rserve-by-HTTP in the Analytics Extensions connection dialog.
   */
  // Created: 2026-07-08 jichikawa (W-20407526)
  export const analyticsExtensionRserveHttpEnabled = false;

  /**
   * Enables broadcast UI FF
   */
  // Created 2022-08-30 ltang
  // TODO: W-12322280
  export const broadcastUI = false;

  /**
   * Enables "Broadcast UI over REST" FF
   */
  // Created 2023-01-24 jratterman
  // TODO: W-12432395
  export const broadcastUIRest = false;

  /**
   * Per site CMEK scoping flag
   */
  // Created 2022-3-18 vokerlund
  export const cmekAvailableForSite = false;

  /**
   * Per site EKM scoping flag
   */
  // Created 2025-07-28 ndenney
  export const enableExternalKeyManagementEnabled = false;

  /**
   * Per site EKM cred scoping flag
   */
  // Created 2025-10-07 z.shen
  export const externalKeyManagementCredentialsEnabled = false;

  /**
   * Enables to move databases to projects
   */
  // Created: 2022-2-14 rachel.liu
  export const databasesInProjectsPostMVPEnabled = true;

  /**
   * Enable multiple Idp for OAuth connectors on server
   */
  // Created: 2022-2-21 eric.wang
  export const oauthMultipleIdpOnServer = true;

  /**
   * Enable BYO Idp for OAuth connectors on server
   */
  // Created: 2022-9-27 ethan.pyke
  export const oauthBYOIdp = true;

  /**
   * Enable Auto Suspension feature
   */
  // Created: 2022-3-13 ymohammed
  export const autoSuspensionForAcceleratedViews = false;

  /**
   * Check if the Tables in projects feature is enabled
   */
  // Created: 2022-4-7 cchudasama
  export const tablesInProjectsEnabled = false;

  /**
   * Check if the Tables in projects UI feature is enabled
   */
  // Created: 2022-5-20 odangat
  export const tablesInProjectsUIEnabled = false;

  /**
   * Enable usage of refreshSession API call instead of getSessionInfo API
   */
  // Created: 2022-05-03 mbarve
  export const refreshSessionEnabled = true;

  /**
   * Enable site settings UI for management of embedded credential migration configuration
   */
  // Created: 2022-3-30 tristan.bithell
  export const embeddedCredentialMigrationEnabled = true;

  /**
   * Enable External Actions Workflows site setting
   */
  // Created: 2022-07-18 sarah.kim
  // Enabled: 2022-08-11 sarah.kim
  export const workflowSiteControl = true;

  /**
   * Pop the GrantPermissionAfterSharingDialog for all content types
   */
  // Created: 2022-4-27 jvanduyne
  // Enabled: 2022-07-20 jvanduyne
  export const grantPermissionsAfterSharing = true;

  /*
   * Enables mixed content search on connect to dialog
   */
  // Created: 2022-08-08 slella
  export const mixedContentConnectToDialogEnabled = true;

  /**
   * Enabling passing user attributes and evaluating user functions against the attributes
   */
  // Created: 2022-09-19 AndyLiu(zhiheng.liu@tableau.com)
  export const userAttributeFunctionsEnabled = false;


  /**
   * Enables Prep Conductor Awareness materials
   */
  // Created: 2022-10-25 jinli
  export const snowflakeKeyPairAuth = false;

  /**
   * Enables Smarter Search features
   */
  // Created: 2022-10-27 edeiparine
  export const smarterSearchEnabled = true;

  /**
   * Enables Search Drafts features
   */
  // Created: 2023-02-17 edeiparine
  export const searchDraftsEnabled = true;

  /**
   * Routes the site-settings integrations fetch to the admin-only siteIntegrations
   * REST API (adminSiteIntegrations) instead of the public one (W-21349725).
   */
  // Created: 2026-08-06 rippili
  export const siteIntegrationsAdminApiEnabled = false;

  /**
   * Enable Data Orientation admin settings
   */
  // Created: 2022-11-07 jose.sarmiento
  export const dataOrientationAdminSettings = true;

  /*
   * Enables hybrid live and extract virtual connections
   */
  // Created: 2022-11-14 gabriela.pulido
  export const hybridLiveExtractVConnsEnabled = false;

  /*
   * Enables custom domains in site settings
   */
  // Created: 2022-11-09 smalikireddy
  export const adminExpCustomDomainsEnabled = true;

  /**
   * Enable site picker for custom domain sites with CNAME validation.
   * Gated behind SitePickerCustomDomains server feature flag.
   *
   * TFS: a3QEE000002EfyD2AS
   * Created: 2026-05-17
   * Target removal: 2026-11-17
   */
  export const sitePickerCustomDomainsEnabled = true;

  /**
   * Enable the data management awareness dialogs
   */
  // Created 2022-11-29 slella
  export const dmawarenessInExploreEnabled = false;

  /*
   * Enables run history subspace for virtual connections
   */
  // Created: 2022-12-08 gabriela.pulido
  export const vconnRunHistory = false;

  /**
   * Enable Data Story site setting for non-SaaS environments
   */
  // Created: 2022-12-15 tj.lewis
  export const dataStoryAvailableOnPrem = true;

  /**
   * Enables Uni-Directional Microsoft Teams integration
   */
  // Created: 2023-01-23 sarah.kim
  export const msTeamsIntegrationPlatformEnabled = false;

  /**
  * Enable PAT Access Control
  */
  // Created: 2023-02-07 xyin
  export const patCreationControlEnabled = false;

  /**
   * Enable Refresh Token Absolute Expiry as a site-setting
   */
  // Created: 2023-02-07 ruby.werman
  export const refreshTokenAbsoluteExpiryEnabled = false;

  /**
   * Enable search and filter on lineage page
   */
  //created: 2023-03-07 y.ding
  export const searchAndFilterOnLineagePageEnabled = true;

   /**
   * Enable label management
   */
  //created: 2023-04-13 zchai
  export const labelManagementEnabled = false;

  /**
   * Enable the new Data Cloud UI changes
   */
  // Created: 2023-04-20 cchudasama
  export const dataCloudInCatalogMVPEnabled = true;

  /**
   * Enable auto quality monitoring site settings
   */
  // Created: 2023-04-27 linda.nduhiu
  export const autoQualityMonitoringEnabled = true;

  /**
   * Enable labels in Web Authoring
   */
  // Cfreated: 2023-05-04 afeyissa
  export const labelsInWebAuthoringEnabled = true;

   /**
   * Enables the new Edit Group Dialog Action UI
   */
  // Created: 2023-04-20 rjoshi
  export const editGroupRedesignEnabled = true;

  /**
   * Enable Custom Label
   */
  // Cfreated: 2023-06-07 csheng
  export const customLabelCategoriesEnabled = true;

  /**
   * Enable Data Labels for workbooks
   */
  // Created: 2026-06-22 l.zhou
  export const workbookLabelEnabled = false;

  /**
   * Enable AI Labels.
   * Currently supported for workbooks only. Independent of workbookLabelEnabled
   * so AI labels can ship before WARNING / SENSITIVITY / custom on workbooks.
   */
  // Created: 2026-06-23 l.zhou
  export const aiLabelEnabled = false;

  /**
   * Enables gating of feature work for Email Customization Milestone 2 POC
   */
  // Created: 2023-03-27 nicholasshepherd
  export const emailCustomizationEnabled = false;

  /**
   * Enables beacon search in omnibox-search
   */
  // Created: 2023-06-15 sloka
  export const beaconSearchEnabled = false;

  /**
   * Enables Connected Apps Multi-project
   */
  // Created: 2023-07-24 xyin
  export const connectedAppsMultiProjectEnabled = true;

  /**
   * Enables Multi-EAS Per Site
   */
  // Created: 2023-07-25 agao
  export const multiEASPerSiteEnabled = true;

  /**
   * Enables custom schedules for flows
   */
  // Created; 2023-06-05 sharanyapillalamarri, malonso
  export const selfServiceSchedulesForFlow = false;

  /**
   * Enable generated suggestions for content descriptions
   */
  // Created; 2023-08-11 linda.nduhiu
  export const suggestDescriptionsEnabled = true;

  /**
   * Enable Tableau AI site setting
   */
  // Created: 2023-10-20 m.tobitchimi
  export const generativeAiSiteControl = true;

  /**
   * Enable admin lockout with external IdP
   */
  // Created; 2023-11-02 edeiparine
  export const adminLockoutEnabled = true;

  /**
   * Enable site generic OIDC
   */
  // Created; 2023-10-20 george.shin
  export const genericOIDCEnabled = false;

  /**
   * Enable Bridge as a Service
   */
  // Created; 2023-11-16 mahaa.noorani
  export const distributedCloudBridgeAsAServiceEnabled = false;

  /**
   * Enable the block non extract site setting for Pulse
   */
  // Created: 2024-06-11 sixuan.zhou
  export const blockNonExtractSiteControlEnabled = false;

  /**
   * Allow admins to control who sees the Pulse feature UI
   */
  // Created: 2024-01-02 bconway
  export const pulseSiteControlEnabled = true;

  /**
   * Show a message that Bridge Legacy Schedules will be deprecated
   */
  // Created: 2024-01-09 l.zhou
  export const bridgeLegacySchedulesRetirementMessageEnabled = true;

  /**
   * Enable common tabgai infrastructure
   */
  // Created: 2024-01-05 jhallmann
  export const tabGAITokenService = false;

  /**
   * Enable tabgai registration
   */
    // Created: 2024-01-05 jhallmann
  export const tabGAITokenServiceRegistration = true;

  /**
   * Enable tabgai enterprise registration
   */
  // Created: 2024-01-05 jhallmann
  export const tabGAITokenServiceEnterpriseRegistration = true;

  /**
   * Enable tabgai enterprise registration
   */
  // Created: 2024-02-20 jhallmann
  export const ntbueAIEnhancedLanguage = true;

  /**
   * Enable address geocoding
   */
  // Created: 2024-01-29 maps team
  export const addressGeocodingEnabled = false;

    /**
   * Enable link features in UI
   */
  // Created: 2024-03-03 rjoshi
  export const syncToLinkEnabled = false;

  /**
   * Enable virtual connection incremental extract
   */
  // Created: 2024-03-16 nlavender
  export const vconnIncrementalRefreshEnabled = true;

  /**
   * Enable pulse metric definition in catalog
   */
  // Created: 2024-03-20 y.ding
  export const pulseInDataCatalogEnabled = false;

  /**
   * Enable private connect
   */
    // Created: 2024-03-25 jason.liu
  export const enablePrivateConnect = false;

  /**
   * Enable web authoring AI/Einstein Copilot for Tableau(ECT) checkbox in site settings
   */
    // Created: 2024-04-02 xjia
    export const webAuthoringAISettingEnabled = false;

  /**
   * Multiple IdP UI Support
   */
  // Created 2024-03-26 edeiparine
  export const featureFlagForMultipleIDPSupportEnabled = false;

  /**
   * Enable full address geocoding functionality on a site-by-site basis. Controlled by Gater
   */
  // Created: 2024-06-06 maps team
  export const addressGeocodingOnSiteEnabled = false;

  /**
   * Enable username email decoupling
   */
  // Created: 2024-06-10 ugs team
  export const usernameEmailDecouplingEnabled = false;

  /**
   * Enable Recycle bin Feature (content and flows). The server delivers the
   * real value in the SessionInfo featureFlags section as !public.enabled, so
   * the Recycle Bin is on in Tableau Cloud and off in Tableau Public. Now also
   * covers flows -- the separate recycleBinFlowsExpEnabled flag was folded in
   * and retired (W-24334808).
   */
    // Created: 2024-08-01 aadarsh.sampath
  export const recycleBinExpEnabled = true;

  /**
   * Enable an error dialog that is shown when the user logs into multiple sites
   * on the same tableau pod in the same browser window. This is not supported
   * due to our use of cookies.
   */
    // Created: 2024-08-08 james.brady
  export const multiSiteLoginErrorDialogEnabled = false;

  /**
   * Enable the navigation of projects using a new nav menu leveraging the Tree Widget.
   */
    // Created: 2024-08-14 nicholasshepherd
  export const projectTreeEnabled = true;

  /**
   * Enable draggable resize for the Project Tree.
   */
  // Created: 2025-10-06 alexis.anand
  export const resizableProjectTreeEnabled = false;

  /**
   * Block bridge domain for localhost, *.tableau.com, and *.tableau.online.com
   */
  // Created: 2024-07-31 pcui
  export const bridgeBlockTableauDomain = true;

  export const ect_UseSharedOrgForTrialEnabled = false;

  /**
   * Enable serving vizportal static assets via CDN.
   */
    // Created: 2024-09-04 aadarsh.sampath
  export const vizportalStaticAssetsEnabled = false;

  /**
   * Enable Pulse Multilingual Ask
   */
  // Created: 2024-10-08 tj.lewis
  export const ntbueMultilingualAdHocQa = false;

  /**
   * Enable Pulse Discover
   */
  // Created: 2024-10-08 tj.lewis
  export const ntbueDiscoverFromHomepageGroup = false;

  /*
  * Change flow ownership
  */
  // Created: 2025-01-16 liu.r
  export const changeFlowOwnership = true;

  /**
   * Enable Explore in Tableau feature for on prem server
   */
  // Created: 2025-01-24 pclementson
  export const instantAnalyticsOnPremEnabled = false;

  /**
   * Enable ExploreInTableau milestone 3 work for on prem servers
   */
  // Created: 2025-07-02 lauren.lum
  export const instantAnalyticsOnPremMilestone3 = false;

  /**
   * Enables Explore in Tableau for external partners (Databricks & Snowflake)
   */
  // Created: 2025-09-11 lauren.lum
  export const instantAnalyticsSP2 = true;

  /**
   * Enable the ability to resize cards in the card view
   */
  // Created: 2025-02-03 layne.lin
  export const cardResizerEnabled = true;

  /*
   * Enable Multiple Scim Configurations
   */
  // Created: 2025-01-31
  // Enabled: 2025-01-31

  /**
   * Enable calling APIs of tabcld-notifications-svc for in-app notifications
   */
  // Created: 2025-02-20 qzhuang
  export const inAppNotificationEnabled = false;

  /*
   * Enable compact header
   */
  // Created: 2025-01-16 llin
  export const compactHeaderEnabled = true;

  /**
   * Enable the tutorials banner on the home page
   */
  // Created: 2025-05-08 nicholasshepherd
  export const guidedOnboardingBannerEnabled = false;

  /**
   * Enable the walkthrough tutorial type in the guided onboarding banner
   */
  // Created: 2025-05-08 nicholasshepherd
  export const guidedOnboardingWalkThroughsEnabled = false;

  export const multipleScimConfigurationsEnabled = false;

  /**
   *
   */
  // Created: 2025-02-27 yxing
  export const redirectWebstoreConfirmationDialogEnabled = false;

  /*
   * Linking flow content page to Job page
   */
  // Created: 2025-03-20 jing.xie
  export const flowContentLinkToJobPageEnabled = true;

  /*
   * Enable tab agent for prep flows
   */
  // Created: 2025-03-26 shalu.yadav
  export const einsteinCopilotForPrep = true;

  /**
   * Enable data connect
   */
  // Created: 2025-08-26 animesh.tiwari
  export const enableDataConnect = false;

  /*
   * Enable Flat file for Data Connect
   */
  // Created: 2025-04-12 bin.liu
  export const enableDataConnectFlatFile = true;

  /*
   * Hyper Minerva as a Service
   */
  // Created: 2025-04-11 liu.r
  export const publishReducedDatasourceEnabled = true;

  /*
   * Enable new tableau built extension site settings
   */
  // Created: 2025-05-12 ljackson
  export const vizExtensionsTrustedTableau = true;

  /*
   * Enable extended UI for published datasources, including dependent datasources count.
   * Server-side flag name: ExtendedPublishedDataSourceUI
   */
  // Created: 2026-01-06 xu.zhao
  export const extendedPublishedDataSourceUIEnabled = false;

  /*
   * Enable new trusted partner extension site settings
   */
  // Created: 2025-05-12 ljackson
  export const vizExtensionsTrustedPartner = true;

  /*
   * Enable security tab in site settings
   */
  // Created: 2025-05-23 razaria
  export const securitySettingsEnabled = false;

  /**
   * Enable Feature Flag for Dark Mode Phase 1 - Viewer Controlled Themes.
   */
    // Created: 2025-06-02 aadarsh.sampath
  export const viewerControlledThemes_ViewingModeEnabled = false;

  /**
   * Enable Feature Flag for Viz Area theme in Viewer Controlled Themes.
   */
  // Created: 2026-04-21 ckirihara
  export const viewerControlledThemes_VizAreaEnabled = false;

  /**
   * Enable Feature Flag for High Contrast option in Viewer Controlled Themes.
   */
  // Created: 2026-03-16 ckirihara
  export const viewerControlledThemes_HighContrastEnabled = false;

  /**
   * Enable Feature Flag for Draft Expiration on UI - EnableDraftExpirationUI.
   */
    // Created: 2025-10-29 csahukargurumurthy
  export const EnableDraftExpirationEnabled = false;

  /**
   * Enable IPAMS in site settings
   */
  // Created: 2025-06-13 razaria
  export const ipFilteringEnabled = false;

    /**
   * Enable MS Teams notifications for Pulse
   */
  // Created: 2025-09-03 sgangisetty
  export const ntbue_ALLOW_MSTEAMS_NOTIFICATIONSEnabled = false;

  /**
   * Enable the Pulse Definition Authoring Assistant
   */
  // Created: 2026-07-17 Pulse Web Experiences
  export const ntbueDefinitionAuthoringAssistantEnabled = false;

  /*
  * Enable Scim Site Settings
  */
  // Created: 2025-06-03 smritigupta
  export const scimSiteSettingsEnabled = true;

   /*
   * Enable agent permissions table
   */
  // Created: 2025-07-10 jrose
  export const agentPermissionsTableEnabled = false;

  /*
   * Enable agent permissions table on Tableau Server (on-prem). Distinct from the
   * Cloud-only agentPermissionsTableEnabled flag. Backend: AgentPermissionsTableOnPremEnabled
   */
  // Created: 2026-08-03 dpainter
  export const agentPermissionsTableOnPremEnabled = true;

   /**
   * Enable Feature Flag for Recycle Bin Metadata
   */
    // Created: 2025-06-25 paul.oconnor
  export const recycleBinMetadataEnabled = false;

  /*
  * Enable in-database processing
  */
  // Created: 2025-07-14 jmao
  export const inDatabaseProcessingEnabled = false;

  /*
  * Treat Dashboard Inisghts as Beta
  */
  // Created: 2025-07-29 jrose
  export const analyticsAssistantDashboardBetaLabelEnabled = true;

  /*
  * Enable Scim Server Settings
  */
  // Created: 2025-08-07 smritigupta
  export const scimServerSettingsEnabled = false;

  /*
   * Enablegroup functionality for PulseGAI
   */
  // Created: 2025-07-10 jrose
  export const agentsPulseGAIReadyForGroups = false;

  /*
   * Enablegroup functionality for Catalog
   */
  // Created: 2025-07-10 jrose
  export const agentsCatalogReadyForGroups = false;

  /*
  * Enable Dashboard Narratives feature
  */
  // Created: 2025-08-13 adovzhik
  export const analyticsAssistantDashboardInsightsEnabled = false;

  /**
   * Enable AI Access capability in workbook permissions dialog
   */
  // Created: 2026-02-05
  export const analyticsAssistantDashboardQAEnabled = false;

  /**
   * Allow group access for Dashboard Insights in the Tableau AI site setting
   */
  // Created: 2026-08-27
  export const analyticsAssistantDashboardSiteSettingGroupAccessEnabled = false;


  /**
   * Enable CABU capability for dashboard
   */
  // Created: 2026-03-04
  export const analyticsAssistantDashboardQASiteSettingOnPremEnabled = false;

  /**
   * Created: 2025-08-27 vrobinweiler
   *
   * Enable useSafeExternalLinks hook, which rewrites anchor tags containing external URLs.
   */
  export const externalLinkSafetyEnabled = false;

  /*
  * Enable Azure AI On-Prem SP2 (Subscription ID, Resource Group, and Tenant ID)
  */
  // Created: 2025-09-17 dpainter
  export const azureAiOnPremEnabledSP2 = false;

   /*
   * Disable Legacy Schedules with Bridge
   */
  // Created: 2025-09-17 mmogadampalli
  export const disableLegacySchedulesWithBridgeEnabled = false;

  /**
   * Enable auto-generated site settings UI components based on metadata definitions from the monolith
   */
  // Created: 2025-09-29 sloka
  export const metadataDrivenSiteSettingsEnabled = false;

  /**
   * Enable reading/writing site settings from the V2 path (siteSettingsV2) instead of legacy paths
   * Requires metadataDrivenSiteSettingsEnabled to be true for full functionality
   */
  // Created: 2025-11-17 vrobinweiler
  export const vizportalSiteSettingsV2Enabled = true;

  /**
   * Enable Admin Profile users to access the V2 metadata-driven site settings UI.
   * When off, only Server/Site Admins can view and edit site settings; Admin
   * Profile users are blocked at the route and do not see the settings icon.
   * Prerequisite: metadataDrivenSiteSettingsEnabled and vizportalSiteSettingsV2Enabled
   * must also be on — this flag is meaningless without them.
   */
  // Created: 2026-04-27 patrick.green
  export const adminProfilesForSiteSettingsEnabled = false;

  /**
   * Enable a banner at the top of VPC which shows critical alerts to the user
   */
  // Created: 2025-12-11 james.brady
  export const vizportalAlertBannerEnabled = true;

  /*
  * Enable Bring Your Own LLM for Tableau AI Cloud
  */
  // Created: 2026-04-12 ssorokin
  export const bringYourOwnLLMForCloudEnabled = false;

  /**
   * Enable OAuth authentication method for BYOLLM on-prem
   */
  // Created: 2026-06-02 aapinyan
  export const analyticsAssistantBYOLLMOAuthEnabled = false;

  /**
   * Enable Auth Agnostic Scim On Server
   */
  // Created: 2025-09-21 smritigupta
  export const authAgnosticScimOnServerEnabled = true;

  /*
   * Enable site level embedded authentication
   */
  // Created: 2025-11-13 edeiparine
  export const siteLevelEmbeddedAuthenticationEnabled = false;

  export const enableBridgeSpecificJobReportingEnabled = false;

  /*
  * Enable cancellation button for pds jobs after it is sent to bridge
  */
  // Created: 2025-11-19 kumarsuraj
  export const bridgeExtractCancellationEnabled = false;

  /*
   * Enable Admin Profiles feature
   */
  // Created: 2026-02-02 Projects, Sharing, & Permissions
  export const adminPermissionsEnabled = false;

  /*
  * Enable hybrid search query pipeline
  */
  // Created: 2026-03-06 james.brady
  export const ces_HybridSearchQueryEnabled = false;

  /*
  * Enable AI Powered Search.
  */
  // Created: 2026-04-24 layne.lin
  export const ces_AISearchEnabled = false;

  /*
   * Enable online site admin to turn on/off catalog
   */
  // Created: 2026-02-16 datacatalog
  export const catalogSectionOnlineSiteAdminEnabled = false;

  /*
   * Enable site admin to try Tableau AI features
   */
  // Created: 2026-03-05 Pulse Web Experiences
  export const tabAITryItEnabled = false;

  /*
   * Enable site admin to limit Discover to only a specific user group
   */
  // Created: 2026-03-25 Pulse Web Experiences
  export const agentsPulseDiscoverReadyForGroupsEnabled = false;

  /**
   * Show the progress dialog site setting option
   */
  // Created: 2026-05-08 Viz Session Management
  export const enableConfigurableProgressDialogEnabled = false;

  /**
   * Enables viz load measurement instrumentation — captures load timestamp and load type
   * and passes them to vizclient as :vpLoadTimestamp and :vpLoadType URL parameters.
   */
  // Created: 2026-05-14 Viz Session Management
  export const vpVizLoadMeasurementEnabled = false;

  // Enable conditional query routing by projects
  export const edgeRoutingFiltersWithProjectsEnabled = false;

  /*
   * Use the WebClient-provided SCIM base URL (getScimContext) instead of hardcoding
   * scim.online.tableau.com. Required for SCIM to work on custom domains.
   */
  // Created: 2026-05-11 varun.pratap.singh
  export const scimDynamicBaseUrlEnabled = true;

  /**
   * Enables the "Language for Emails" site setting. Online-only.
   */
  // Created: 2026-06-12 smritigupta
  export const uiamEmailLocalizationEnabled = false;

  /**
   * Enables the Notes column in the revision history dialog for workbooks and datasources.
   */
  // Created: 2026-06-17 xianqu
  export const enablePublishRevisionNotes = false;

  /**
   * Enables Admin Insights PFT (Product Feature Telemetry) events for content
   * access (workbook/datasource/flow view, publish, refresh, download, etc.).
   * Gates all emitters in AdminInsightsPftEmitter.
   */
  // Created: 2026-06-30 dheerendra.kumar
  export const adminInsightsPftTelemetryEnabled = true;

  /**
   * Support integration with external metric providers
   */
  // Created: 2026-06-24 haya.gur
  export const externalMetricsIntegration_LookerDbtEnabled = false;

  /*
   * W-19373381: show a warning in the Edit-Connection dialog on insecure (HTTP) installs that
   * data-connection credentials are transmitted unprotected, since client-side password encryption
   * was removed from this flow. Gated so the warning can be rolled out independently of the fix.
   */
  // Created: 2026-06-24 arandive
  export const insecureConnectionCredentialWarningEnabled = false;

  /**
   * Enable independent PULSE_METRIC_CERTIFY permission capability
   * When OFF: UI hides the certification permission capability
   * When ON: UI shows certification capability in permissions dialog
   */
  // Created: 2026-07-30 cobry
  export const ntbue_CHECK_METRIC_CERTIFY_CAPEnabled = false;

  /**
   * Enables the Content Mobility (Content Relay) site settings section.
   * Gate: tabhfcontentmobility_cloudenabled
   * When OFF: Content Mobility settings section is hidden
   * When ON: Content Mobility settings section is visible for site/server admins in online environments
   */
  // Created: 2026-08-26 tdemoraisribeiro
  export const tabhfcontentmobility_cloudenabledEnabled = false;

  /**
   * Enables the Cloud-appropriate Guest Access site-setting checkbox (Site Admin
   * Settings > General). Development flag ahead of Area 5a/5b (TCM entitlement
   * gating) landing; removal is a follow-up gated on that work.
   */
  // Created: 2026-09-16 jeff.orr
  export const cloudGuestAccessEnabled = false;

  /*
  * Flow cancellation on flow page
  * Gate: flow_cancellation_on_flow_page
  */
  // Created: 2026-09-01 zgao
  export const flow_cancellation_on_flow_pageEnabled = false;
}

////////////////////////////////////////////////////////////
///////////READ THIS BEFORE ADDING ANY NEW FLAGS////////////
///////////READ THIS BEFORE ADDING ANY NEW FLAGS////////////
///////////////////////////////////////////////////////////
//  Note: If you defined your feature flag in the monolith, the first letter of your feature flag has
//  be lower case here, and "Enabled" has added to the suffix. Even if you define it with an uppercase
//  first letter elsewhere, it will be transformed to have a lower case first letter and an "Enabled"
//  suffix when delivered to VPC for overrides.
//  See https://confluence.internal.salesforce.com/spaces/devprocess/pages/529054092/Feature+Flags+in+Server+Back-end

// Override the feature flags as soon as possible after the page loads and the feature flag defaults
// are set, before the application is initialized. The helper must be invoked after declaration of
// the feature flags, otherwise there will not be existing values to overwrite
if (window.location.search) {
  FeatureFlagsHelper.overwriteFeatureFlagsFromQueryString(window.location.search, FeatureFlags);
}
